import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ExceptionService, PaginatedList, UtilsService } from '../../xform-compat';
import { RmsBasicCrudService } from '../components/rms-basic-crud.service';
import { CompanyBasic, Product, EntityCategory, ProductCategoryValue, ProductInstance } from '../../shared/models';
import { CONFIG } from '../../config';


/**
 * Service to handle products API integration
 */
@Injectable({
  providedIn: 'root'
})
export class ProductService extends RmsBasicCrudService<any> {

  constructor(
    http: HttpClient,
    utilsService: UtilsService,
    exceptionService: ExceptionService
  ) {
    super(http, utilsService, exceptionService);
  }

  /**
   * Process the request to create and new Product
   * @param {Product} body
   * @returns {Observable<Product>}
   */
  public createProduct (body: Product): Observable<Product> {
    return super.saveData(`/api/rms/products`, body);
  }

  /**
   * Gets the list of products
   */
  public getProducts (): Observable<Product> {
    return super.getData(`/api/rms/products`);
  }

  /**
   * Updates baseic product info
   * @param {number} id
   * @param {Product} body
   * @returns {Observable<Product>}
   */
  public updateProduct (id: number, body: Product): Observable<Product> {
    return super.updateData(`/api/rms/products/${id}`, body);
  }

  /**
   * Gets basic product info
   * @param {number} id
   * @param params
   * @returns {Observable<Product>}
   */
  public getProduct (id: number, params?: any): Observable<Product> {
    return super.getData(`/api/rms/products/${id}`, params);
  }

  /**
   * Gets rms and map product instances
   * @param {number} id
   * @returns {Observable<PaginatedList<ProductInstance>>}
   */
  public getProductInstances (id: number): Observable<PaginatedList<ProductInstance>> {
    return super.getData(`/api/rms/products/${id}/instances?pagesize=${CONFIG.pagination.singleLargePage}`);
  }

  /**
   * Get all the categories or protected categories if the para protected = true is present
   * @param params
   * @returns {Observable<PaginatedList<EntityCategory>>}
   */
  public getCategories (params?: any): Observable<PaginatedList<EntityCategory>> {
    return super.getData(`/api/rms/categories`, params);
  }

  /**
   * Autocomplete to load product categories
   * @param params
   * @returns {Observable<any>}
   */
  public categoryAutocomplete (params?: any): Observable<PaginatedList<EntityCategory>> {
    return super.getData(`/api/rms/autocomplete/categories`, params);
  }

  /**
   * Used for search and autocomplete category values
   * used to populate category autocomplete value fields
   * @param params
   * @returns {Observable<any>}
   */
  public categoryValueAutocomplete (params?: any): Observable<PaginatedList<ProductCategoryValue>> {
    return super.getData(`/api/rms/autocomplete/categoryvalues`, params);
  }

  /**
   * Deactivates a product instance
   * @param {number} prodId
   * @param {number} instanceId
   * @returns {Observable<null>}: status code 2014
   */
  public deactivateInstance (prodId: number, instanceId: number): Observable<null> {
    return super.saveData(`/api/rms/products/${prodId}/instances/${instanceId}/deactivate`, {});
  }

  /**
   * Creates a new rms product instance
   * @param {number} productId
   * @param body
   * @returns {Observable<ProductInstance>}
   */
  public createProductInstance (productId: number, body: any): Observable<ProductInstance> {
    return super.saveData(`/api/rms/products/${productId}/instances`, body);
  }

  /**
   * Updates the rms product instance
   * @param {number} productId
   * @param {number} instanceId
   * @param body
   * @returns {Observable<ProductInstance>}
   */
  public updateProductInstance (productId: number, instanceId: number, body: any): Observable<ProductInstance> {
    return super.updateData(`/api/rms/products/${productId}/instances/${instanceId}`, body);
  }

  /**
   * Search over all the companies
   * @param params: search term `q` and pagination params
   * @returns {Observable<PaginatedList<CompanyBasic>>}
   */
  public searchProduct (params?: any): Observable<PaginatedList<Product>> {
    return super.getData(`/api/rms/autocomplete/products`, params);
  }
}
